import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { api } from '../utils/api';

const TIPOS = [
  { value: 'accidente', label: 'Accidente', color: '#ef4444' },
  { value: 'enfermedad', label: 'Enfermedad', color: '#f59e0b' },
  { value: 'medicacion', label: 'Medicación', color: '#3b82f6' },
  { value: 'conducta', label: 'Conducta', color: '#8b5cf6' },
  { value: 'otro', label: 'Otro', color: '#6b7280' },
];

const GRAVEDAD = [
  { value: 'leve', label: 'Leve', color: '#22c55e' },
  { value: 'moderada', label: 'Moderada', color: '#f59e0b' },
  { value: 'grave', label: 'Grave', color: '#ef4444' },
];

export default function AdminIncidencias() {
  const { token } = useAuth();
  const [incidencias, setIncidencias] = useState([]);
  const [eventos, setEventos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterEvento, setFilterEvento] = useState('');
  const [filterTipo, setFilterTipo] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [eventMembers, setEventMembers] = useState([]);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [detail, setDetail] = useState(null);
  const [form, setForm] = useState({
    eventoId: '', eventoNombre: '', miembroNombre: '', miembroEdad: '',
    personaId: null, bookingId: '',
    tipo: 'accidente', descripcion: '', gravedad: 'leve',
    fechaHora: new Date().toISOString().slice(0, 16),
    lugarExacto: '', testigos: '', accionesTomadas: '',
    requiereNotificacionFamilia: false,
  });

  useEffect(() => {
    loadData();
  }, []);

  async function loadData() {
    setLoading(true);
    try {
      const [inc, evts] = await Promise.all([
        api.getIncidencias(token),
        api.getAdminEvents(token),
      ]);
      setIncidencias(inc.incidencias || []);
      setEventos(evts.events || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  }

  async function loadEventMembers(eventId) {
    if (!eventId) { setEventMembers([]); return; }
    setLoadingMembers(true);
    try {
      const r = await api.getEventMembers(token, eventId);
      setEventMembers(r.members || []);
    } catch (e) { console.error(e); setEventMembers([]); }
    setLoadingMembers(false);
  }

  function selectMember(member) {
    setForm(p => ({
      ...p,
      miembroNombre: member.personaNombre || member.name,
      miembroEdad: member.personaEdad || member.age || '',
      personaId: member.personaId || null,
      bookingId: member.bookingId || '',
    }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.eventoId || !form.miembroNombre || !form.descripcion) {
      alert('Rellena evento, nombre y descripcion');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        miembroEdad: form.miembroEdad ? parseInt(form.miembroEdad) : null,
        testigos: form.testigos ? form.testigos.split(',').map(t => t.trim()).filter(Boolean) : [],
      };
      // Limpiar campos internos del form
      delete payload.bookingId;
      if (!payload.personaId) delete payload.personaId;
      await api.createIncidencia(token, payload);
      setShowForm(false);
      setEventMembers([]);
      setForm({
        eventoId: '', eventoNombre: '', miembroNombre: '', miembroEdad: '',
        personaId: null, bookingId: '',
        tipo: 'accidente', descripcion: '', gravedad: 'leve',
        fechaHora: new Date().toISOString().slice(0, 16),
        lugarExacto: '', testigos: '', accionesTomadas: '',
        requiereNotificacionFamilia: false,
      });
      loadData();
    } catch (err) { alert('Error: ' + err.message); }
    setSaving(false);
  }

  const filtered = incidencias.filter(i => {
    if (filterEvento && i.eventoId !== filterEvento) return false;
    if (filterTipo && i.tipo !== filterTipo) return false;
    return true;
  });

  const stats = {
    total: incidencias.length,
    graves: incidencias.filter(i => i.gravedad === 'grave').length,
    pendientes: incidencias.filter(i => i.requiereNotificacionFamilia && !i.notificado).length,
  };

  if (loading) return <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--mid)' }}>Cargando...</div>;

  return (
    <div style={{ padding: '1.5rem', maxWidth: 1100, margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
        <div>
          <h1 style={{ fontSize: '1.5rem', fontWeight: 800, color: 'var(--white)' }}>Incidencias</h1>
          <p style={{ fontSize: '.85rem', color: 'var(--mid)' }}>Registro de incidencias y accidentes</p>
        </div>
        <button className="btn btn-primary" onClick={() => setShowForm(true)}>+ Nueva incidencia</button>
      </div>

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem', marginBottom: '1.5rem' }}>
        {[
          { label: 'Total incidencias', value: stats.total, color: 'var(--sage)' },
          { label: 'Graves', value: stats.graves, color: '#ef4444' },
          { label: 'Pendientes notificar', value: stats.pendientes, color: '#f59e0b' },
        ].map(s => (
          <div key={s.label} style={{ background: 'rgba(255,255,255,.04)', border: '1px solid rgba(122,171,138,.15)', borderRadius: 'var(--radius)', padding: '1rem', textAlign: 'center' }}>
            <div style={{ fontSize: '1.8rem', fontWeight: 800, color: s.color, fontFamily: "'Fraunces',serif" }}>{s.value}</div>
            <div style={{ fontSize: '.75rem', color: 'var(--mid)', textTransform: 'uppercase', letterSpacing: '.08em' }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: '.8rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
        <select className="form-select" value={filterEvento} onChange={e => setFilterEvento(e.target.value)}
          style={{ maxWidth: 250 }}>
          <option value="">Todos los eventos</option>
          {eventos.map(ev => <option key={ev.id} value={ev.id}>{ev.name}</option>)}
        </select>
        <select className="form-select" value={filterTipo} onChange={e => setFilterTipo(e.target.value)}
          style={{ maxWidth: 180 }}>
          <option value="">Todos los tipos</option>
          {TIPOS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>
      </div>

      {/* List */}
      {filtered.length === 0 ? (
        <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--mid)' }}>
          No hay incidencias registradas{filterEvento || filterTipo ? ' con estos filtros' : ''}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '.8rem' }}>
          {filtered.map(inc => {
            const tipoInfo = TIPOS.find(t => t.value === inc.tipo) || TIPOS[4];
            const gravInfo = GRAVEDAD.find(g => g.value === inc.gravedad) || GRAVEDAD[0];
            let fecha = '';
            try {
              const d = inc.fechaHora?.toDate ? inc.fechaHora.toDate() : inc.fechaHora?._seconds ? new Date(inc.fechaHora._seconds * 1000) : null;
              if (d) fecha = d.toLocaleString('es-ES', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
            } catch {}
            return (
              <div key={inc.id} style={{
                background: 'rgba(255,255,255,.04)', border: '1px solid rgba(122,171,138,.15)',
                borderLeft: `4px solid ${gravInfo.color}`, borderRadius: 'var(--radius)', padding: '1rem',
                cursor: 'pointer',
              }} onClick={() => setDetail(inc)}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '.5rem' }}>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '.5rem', marginBottom: '.3rem' }}>
                      <span style={{ fontWeight: 700, fontSize: '1rem', color: 'var(--white)' }}>{inc.miembroNombre}</span>
                      {inc.miembroEdad && <span style={{ fontSize: '.75rem', color: 'var(--mid)' }}>({inc.miembroEdad} a.)</span>}
                      {inc.personaId && (
                        <span className="badge" style={{
                          background: 'rgba(122,171,138,.15)', color: 'var(--sage)',
                          border: '1px solid rgba(122,171,138,.3)', fontSize: '.6rem',
                        }}>vinculado</span>
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: '.4rem', flexWrap: 'wrap' }}>
                      <span className="badge" style={{ background: tipoInfo.color + '22', color: tipoInfo.color, border: `1px solid ${tipoInfo.color}44`, fontSize: '.68rem' }}>
                        {tipoInfo.label}
                      </span>
                      <span className="badge" style={{ background: gravInfo.color + '22', color: gravInfo.color, border: `1px solid ${gravInfo.color}44`, fontSize: '.68rem' }}>
                        {gravInfo.label}
                      </span>
                      {inc.requiereNotificacionFamilia && !inc.notificado && (
                        <span className="badge badge-amber" style={{ fontSize: '.68rem' }}>Pendiente notificar</span>
                      )}
                    </div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: '.75rem', color: 'var(--mid)' }}>{fecha}</div>
                    <div style={{ fontSize: '.72rem', color: 'var(--sage)' }}>{inc.eventoNombre}</div>
                  </div>
                </div>
                <p style={{ margin: '.6rem 0 0', fontSize: '.88rem', color: 'var(--white)', lineHeight: 1.5 }}>{inc.descripcion}</p>
                {inc.accionesTomadas && (
                  <p style={{ margin: '.4rem 0 0', fontSize: '.82rem', color: 'var(--sage)' }}>
                    Acciones: {inc.accionesTomadas}
                  </p>
                )}
                {inc.lugarExacto && (
                  <p style={{ margin: '.2rem 0 0', fontSize: '.78rem', color: 'var(--mid)' }}>Lugar: {inc.lugarExacto}</p>
                )}
                {inc.testigos?.length > 0 && (
                  <p style={{ margin: '.2rem 0 0', fontSize: '.78rem', color: 'var(--mid)' }}>Testigos: {inc.testigos.join(', ')}</p>
                )}
                <div style={{ marginTop: '.6rem', display: 'flex', gap: '.4rem' }}>
                  {inc.requiereNotificacionFamilia && !inc.notificado && (
                    <button className="btn btn-ghost btn-sm" onClick={async (e) => {
                      e.stopPropagation();
                      await api.updateIncidencia(token, inc.id, { notificado: true });
                      loadData();
                    }}>Marcar notificado</button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Detail modal */}
      {detail && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setDetail(null)}>
          <div className="modal modal-lg">
            <div className="modal-header">
              <div>
                <div className="section-label">Detalle de incidencia</div>
                <h2 style={{ margin: 0 }}>{detail.miembroNombre}</h2>
              </div>
              <button className="modal-close" onClick={() => setDetail(null)}>x</button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '.6rem .8rem', marginBottom: '1rem' }}>
              {[
                ['Evento', detail.eventoNombre],
                ['Edad', detail.miembroEdad ? `${detail.miembroEdad} anos` : null],
                ['Tipo', TIPOS.find(t => t.value === detail.tipo)?.label || detail.tipo],
                ['Gravedad', GRAVEDAD.find(g => g.value === detail.gravedad)?.label || detail.gravedad],
                ['Lugar', detail.lugarExacto],
                ['Registrado por', detail.registradoPorNombre],
              ].filter(([, v]) => v).map(([label, value]) => (
                <div key={label}>
                  <div style={{ fontSize: '.65rem', fontWeight: 600, letterSpacing: '.08em', textTransform: 'uppercase', color: 'var(--mid)', marginBottom: '.1rem' }}>{label}</div>
                  <div style={{ fontSize: '.88rem', color: 'var(--white)' }}>{value}</div>
                </div>
              ))}
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <div style={{ fontSize: '.65rem', fontWeight: 600, letterSpacing: '.08em', textTransform: 'uppercase', color: 'var(--mid)', marginBottom: '.2rem' }}>Descripcion</div>
              <p style={{ fontSize: '.88rem', color: 'var(--white)', lineHeight: 1.6, margin: 0, whiteSpace: 'pre-wrap' }}>{detail.descripcion}</p>
            </div>

            {detail.accionesTomadas && (
              <div style={{ marginBottom: '1rem' }}>
                <div style={{ fontSize: '.65rem', fontWeight: 600, letterSpacing: '.08em', textTransform: 'uppercase', color: 'var(--mid)', marginBottom: '.2rem' }}>Acciones tomadas</div>
                <p style={{ fontSize: '.88rem', color: 'var(--sage)', lineHeight: 1.5, margin: 0 }}>{detail.accionesTomadas}</p>
              </div>
            )}

            {detail.testigos?.length > 0 && (
              <div style={{ marginBottom: '1rem' }}>
                <div style={{ fontSize: '.65rem', fontWeight: 600, letterSpacing: '.08em', textTransform: 'uppercase', color: 'var(--mid)', marginBottom: '.2rem' }}>Testigos</div>
                <p style={{ fontSize: '.88rem', color: 'var(--white)', margin: 0 }}>{detail.testigos.join(', ')}</p>
              </div>
            )}

            {/* Datos de salud de la persona vinculada */}
            {detail.persona && (
              <div style={{ marginTop: '.5rem', padding: '.8rem', background: 'rgba(122,171,138,.07)', borderRadius: 'var(--radius)', border: '1px solid rgba(122,171,138,.15)' }}>
                <div style={{ fontSize: '.7rem', fontWeight: 700, letterSpacing: '.1em', textTransform: 'uppercase', color: 'var(--sage)', marginBottom: '.5rem' }}>
                  Datos de salud (persona vinculada)
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '.4rem .8rem' }}>
                  {[
                    ['Alergias', detail.persona.alergias],
                    ['Medicacion', detail.persona.medicacionActual],
                    ['Enfermedades', detail.persona.enfermedades],
                  ].map(([label, val]) => (
                    <div key={label}>
                      <div style={{ fontSize: '.62rem', fontWeight: 600, letterSpacing: '.08em', textTransform: 'uppercase', color: 'var(--mid)' }}>{label}</div>
                      <div style={{ fontSize: '.82rem', color: val ? '#f59e0b' : 'var(--mid)', fontStyle: val ? 'normal' : 'italic' }}>{val || 'No indicado'}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div style={{ display: 'flex', gap: '.6rem', marginTop: '1rem' }}>
              {detail.requiereNotificacionFamilia && !detail.notificado && (
                <button className="btn btn-primary" onClick={async () => {
                  await api.updateIncidencia(token, detail.id, { notificado: true });
                  setDetail(null);
                  loadData();
                }}>Marcar como notificado</button>
              )}
              <button className="btn btn-ghost" onClick={() => setDetail(null)}>Cerrar</button>
            </div>
          </div>
        </div>
      )}

      {/* Create form modal */}
      {showForm && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowForm(false)}>
          <div className="modal modal-lg">
            <div className="modal-header">
              <div className="section-label">Nueva incidencia</div>
              <button className="modal-close" onClick={() => setShowForm(false)}>x</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="form-row-2">
                <div className="form-group">
                  <label className="form-label">Evento <span className="req">*</span></label>
                  <select className="form-select" value={form.eventoId} onChange={e => {
                    const ev = eventos.find(ev => ev.id === e.target.value);
                    setForm(p => ({
                      ...p, eventoId: e.target.value, eventoNombre: ev?.name || '',
                      miembroNombre: '', miembroEdad: '', personaId: null, bookingId: '',
                    }));
                    loadEventMembers(e.target.value);
                  }} required>
                    <option value="">Selecciona evento</option>
                    {eventos.filter(e => e.status === 'active').map(ev => (
                      <option key={ev.id} value={ev.id}>{ev.name}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Fecha y hora</label>
                  <input className="form-input" type="datetime-local" value={form.fechaHora}
                    onChange={e => setForm(p => ({ ...p, fechaHora: e.target.value }))} />
                </div>
              </div>

              {/* Selector de miembro inscrito */}
              {form.eventoId && (
                <div className="form-group">
                  <label className="form-label">Persona afectada</label>
                  {loadingMembers ? (
                    <div style={{ fontSize: '.82rem', color: 'var(--mid)', padding: '.4rem 0' }}>Cargando inscritos...</div>
                  ) : eventMembers.length > 0 ? (
                    <div style={{ display: 'flex', gap: '.4rem', flexWrap: 'wrap', marginBottom: '.5rem' }}>
                      {eventMembers.map((m, idx) => {
                        const nombre = m.personaNombre || m.name;
                        const isSelected = form.miembroNombre === nombre;
                        return (
                          <button key={`${m.bookingId}-${m.memberIndex}`} type="button"
                            onClick={() => selectMember(m)}
                            style={{
                              padding: '.3rem .7rem', borderRadius: '20px', fontSize: '.78rem',
                              border: isSelected ? '2px solid var(--sage)' : '1px solid rgba(122,171,138,.25)',
                              background: isSelected ? 'rgba(122,171,138,.2)' : 'rgba(255,255,255,.04)',
                              color: isSelected ? 'var(--sage)' : 'var(--white)',
                              cursor: 'pointer', transition: 'all .15s',
                            }}
                          >
                            {nombre}
                            {m.age || m.personaEdad ? ` (${m.personaEdad || m.age}a)` : ''}
                            {m.personaId && <span style={{ marginLeft: '.3rem', fontSize: '.65rem', opacity: .6 }}>vinculado</span>}
                          </button>
                        );
                      })}
                    </div>
                  ) : (
                    <div style={{ fontSize: '.78rem', color: 'var(--mid)', marginBottom: '.3rem' }}>
                      No hay inscritos en este evento. Introduce el nombre manualmente.
                    </div>
                  )}
                </div>
              )}

              <div className="form-row-2">
                <div className="form-group">
                  <label className="form-label">Nombre del afectado <span className="req">*</span></label>
                  <input className="form-input" value={form.miembroNombre}
                    onChange={e => setForm(p => ({ ...p, miembroNombre: e.target.value, personaId: null }))} required placeholder="Nombre completo" />
                </div>
                <div className="form-group">
                  <label className="form-label">Edad</label>
                  <input className="form-input" type="number" min="0" max="99" value={form.miembroEdad}
                    onChange={e => setForm(p => ({ ...p, miembroEdad: e.target.value }))} placeholder="Edad" />
                </div>
              </div>
              <div className="form-row-2">
                <div className="form-group">
                  <label className="form-label">Tipo</label>
                  <select className="form-select" value={form.tipo} onChange={e => setForm(p => ({ ...p, tipo: e.target.value }))}>
                    {TIPOS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Gravedad</label>
                  <select className="form-select" value={form.gravedad} onChange={e => setForm(p => ({ ...p, gravedad: e.target.value }))}>
                    {GRAVEDAD.map(g => <option key={g.value} value={g.value}>{g.label}</option>)}
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Descripcion <span className="req">*</span></label>
                <textarea className="form-textarea" value={form.descripcion} rows={3}
                  onChange={e => setForm(p => ({ ...p, descripcion: e.target.value }))} required
                  placeholder="Describe detalladamente lo ocurrido" />
              </div>
              <div className="form-group">
                <label className="form-label">Lugar exacto</label>
                <input className="form-input" value={form.lugarExacto}
                  onChange={e => setForm(p => ({ ...p, lugarExacto: e.target.value }))} placeholder="Ej: Piscina, Comedor, Sala de actividades..." />
              </div>
              <div className="form-group">
                <label className="form-label">Acciones tomadas</label>
                <textarea className="form-textarea" value={form.accionesTomadas} rows={2}
                  onChange={e => setForm(p => ({ ...p, accionesTomadas: e.target.value }))}
                  placeholder="Primeros auxilios aplicados, derivacion a centro medico..." />
              </div>
              <div className="form-group">
                <label className="form-label">Testigos (separados por comas)</label>
                <input className="form-input" value={form.testigos}
                  onChange={e => setForm(p => ({ ...p, testigos: e.target.value }))} placeholder="Monitor Juan, Monitora Ana" />
              </div>
              <div className="form-group" style={{ marginBottom: '1rem' }}>
                <label className="toggle-wrap">
                  <input type="checkbox" checked={form.requiereNotificacionFamilia}
                    onChange={e => setForm(p => ({ ...p, requiereNotificacionFamilia: e.target.checked }))} />
                  <span className="toggle-track"><span className="toggle-thumb" /></span>
                  <span style={{ fontSize: '.88rem' }}>Requiere notificacion a la familia</span>
                </label>
              </div>
              <div style={{ display: 'flex', gap: '.8rem' }}>
                <button type="button" className="btn btn-ghost btn-full" onClick={() => setShowForm(false)}>Cancelar</button>
                <button type="submit" className="btn btn-primary btn-full" disabled={saving}>
                  {saving ? 'Guardando...' : 'Registrar incidencia'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
